import { useState } from 'react'
import { SkeletonList } from '../componentes/Skeleton'
import { EstadoError } from '../componentes/EstadoError'
import { EstadoVacio } from '../componentes/EstadoVacio'
import { IconoActualizar } from '../componentes/iconos'
import {
  useWorkflows,
  useEjecuciones,
  useCambiarEstadoWorkflow,
  type WorkflowResumen,
} from '../hooks/useWorkflows'

/** Píldora de estado activo/inactivo, con los tokens de tema del panel. */
function PildoraEstado({ activo }: { activo: boolean }) {
  return (
    <span
      className={[
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium',
        activo
          ? 'bg-positivo-fondo text-positivo-tinta'
          : 'bg-elevado text-tinta-apagada',
      ].join(' ')}
    >
      <span
        className={['size-1.5 rounded-full', activo ? 'bg-positivo-punto' : 'bg-tinta-apagada'].join(' ')}
        aria-hidden
      />
      {activo ? 'Activo' : 'Inactivo'}
    </span>
  )
}

/** Interruptor de activación. Deshabilitado mientras la mutación viaja. */
function Interruptor({
  activo,
  ocupado,
  onCambiar,
  etiqueta,
}: {
  activo: boolean
  ocupado: boolean
  onCambiar: () => void
  etiqueta: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={activo}
      aria-label={etiqueta}
      disabled={ocupado}
      onClick={onCambiar}
      className={[
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-60',
        activo ? 'bg-acento' : 'bg-borde-fuerte',
      ].join(' ')}
    >
      <span
        className={[
          'inline-block size-4 transform rounded-full bg-superficie transition-transform',
          activo ? 'translate-x-6' : 'translate-x-1',
        ].join(' ')}
      />
    </button>
  )
}

function formatearFecha(iso: string | null): string {
  if (!iso) return '--'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '--'
  return d.toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Color del estado de una ejecución. */
function claseEstadoEjecucion(estado: string): string {
  if (estado === 'success') return 'text-positivo-tinta'
  if (estado === 'error') return 'text-critico-tinta'
  return 'text-tinta-apagada'
}

/** Panel lateral con las últimas ejecuciones del workflow seleccionado. */
function PanelEjecuciones({ workflow, onCerrar }: { workflow: WorkflowResumen; onCerrar: () => void }) {
  const ejecuciones = useEjecuciones(workflow.id)

  return (
    <aside
      aria-label={`Ejecuciones de ${workflow.nombre}`}
      className="flex flex-col gap-3 rounded-xl border border-borde bg-superficie p-4"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-tinta">Últimas ejecuciones</h2>
          <p className="mt-0.5 text-xs text-tinta-apagada">{workflow.nombre}</p>
        </div>
        <button
          type="button"
          onClick={onCerrar}
          className="rounded-lg px-2 py-1 text-xs font-medium text-tinta-2 transition-colors hover:bg-elevado"
        >
          Cerrar
        </button>
      </div>

      {ejecuciones.isPending ? (
        <SkeletonList rows={4} />
      ) : ejecuciones.isError ? (
        <EstadoError error={ejecuciones.error} onReintentar={() => void ejecuciones.refetch()} />
      ) : (ejecuciones.data ?? []).length === 0 ? (
        <p className="text-sm text-tinta-apagada">Sin ejecuciones registradas.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-borde">
          {ejecuciones.data!.map((e) => (
            <li key={e.id} className="flex items-center justify-between gap-3 py-2 text-sm">
              <span className={['font-medium', claseEstadoEjecucion(e.estado)].join(' ')}>
                {e.estado}
              </span>
              <span className="text-xs text-tinta-apagada">{e.modo}</span>
              <span className="text-xs text-tinta-2">{formatearFecha(e.iniciadaEn)}</span>
            </li>
          ))}
        </ul>
      )}
    </aside>
  )
}

export function Workflows() {
  const workflows = useWorkflows()
  const cambiarEstado = useCambiarEstadoWorkflow()
  const [seleccionado, setSeleccionado] = useState<WorkflowResumen | null>(null)

  const filas = workflows.data ?? []
  const activos = filas.filter((w) => w.activo).length

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-tinta">Workflows</h1>
          <p className="mt-1 text-sm text-tinta-apagada">
            Automatizaciones de n8n: actívalas o desactívalas y revisa sus últimas ejecuciones sin
            salir del panel.
          </p>
        </div>
        <button
          type="button"
          aria-label="Actualizar la lista"
          onClick={() => void workflows.refetch()}
          className="flex items-center gap-2 rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado"
        >
          <IconoActualizar className="size-4" />
          <span className="hidden sm:inline">Actualizar</span>
        </button>
      </div>

      {!workflows.isPending && !workflows.isError ? (
        <p className="text-sm text-tinta-apagada">
          {filas.length} workflows · <span className="text-positivo-tinta">{activos} activos</span>
        </p>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        <section aria-label="Lista de workflows" className="min-w-0">
          {workflows.isPending ? (
            <SkeletonList />
          ) : workflows.isError ? (
            <EstadoError error={workflows.error} onReintentar={() => void workflows.refetch()} />
          ) : filas.length === 0 ? (
            <EstadoVacio
              titulo="No hay workflows"
              descripcion="La API de n8n no ha devuelto ningún workflow."
            />
          ) : (
            <ul className="flex flex-col gap-2">
              {filas.map((w) => {
                const ocupado = cambiarEstado.isPending && cambiarEstado.variables?.id === w.id
                const activa = seleccionado?.id === w.id
                return (
                  <li
                    key={w.id}
                    className={[
                      'flex items-center gap-3 rounded-xl border bg-superficie px-4 py-3 transition-colors',
                      activa ? 'border-acento' : 'border-borde hover:bg-elevado',
                    ].join(' ')}
                  >
                    <button
                      type="button"
                      onClick={() => setSeleccionado(w)}
                      className="flex min-w-0 flex-1 flex-col items-start gap-1 text-left"
                    >
                      <span className="truncate text-sm font-medium text-tinta">{w.nombre}</span>
                      <span className="flex items-center gap-2">
                        <PildoraEstado activo={w.activo} />
                        {w.tags.slice(0, 3).map((t) => (
                          <span
                            key={t}
                            className="rounded-md bg-elevado px-1.5 py-0.5 text-[11px] text-tinta-apagada"
                          >
                            {t}
                          </span>
                        ))}
                      </span>
                    </button>
                    <Interruptor
                      activo={w.activo}
                      ocupado={ocupado}
                      etiqueta={`${w.activo ? 'Desactivar' : 'Activar'} ${w.nombre}`}
                      onCambiar={() => cambiarEstado.mutate({ id: w.id, activo: !w.activo })}
                    />
                  </li>
                )
              })}
            </ul>
          )}

          {cambiarEstado.isError ? (
            <p role="alert" className="mt-2 text-sm text-critico-tinta">
              No se ha podido cambiar el estado del workflow. Inténtalo de nuevo.
            </p>
          ) : null}
        </section>

        {seleccionado ? (
          <PanelEjecuciones workflow={seleccionado} onCerrar={() => setSeleccionado(null)} />
        ) : (
          <aside className="hidden rounded-xl border border-dashed border-borde p-4 text-sm text-tinta-apagada lg:block">
            Selecciona un workflow para ver sus últimas ejecuciones.
          </aside>
        )}
      </div>
    </div>
  )
}
